import axios from "axios";
import { jwtDecode } from 'jwt-decode';
import { AuthToken } from "../entities/User";
import { ProgramContainer, BpsConfig } from "../entities/formTemplateDataType";
import { clearSelectedService } from "../commonComponent/TileSelectionComponent";
import { getEnvVar } from "../config/RuntimeConfig";

const MaxTry = 3;
const SIGNIN_ATTEMPT_PREFIX = 'bps_signinAttempts_';
const BPS_SIGNIN_IN_FLIGHT = 'bps_signin_in_flight';
const BPS_AUTH_TOKENS = 'bps_authTokens';
const BPS_USER_PROFILE = 'bps_userProfile';
const BPS_CONFIG_KEY = 'bps_config';
const BPS_CODE_VERIFIER = 'bps_codeVerifier';
const BPS_APP_ID = 'bps_appId';
const BPS_LANG_KEY = 'bps_isEng';

const BPS_BASE_URL = getEnvVar('BPS_BASE_URL') || '';
const BPS_AUTH_ENDPOINT = '/oauth2/rest/authorize';
const BPS_SCOPE = 'openid email profile';

const getStoredValue = (key: string): string | null => {
    try {
        return sessionStorage.getItem(key);
    } catch {
        return null;
    }
};

const setStoredValue = (key: string, value: string) => {
    try {
        sessionStorage.setItem(key, value);
    } catch {
    }
};

const removeStoredValue = (key: string) => {
    try {
        sessionStorage.removeItem(key);
    } catch {
    }
};

const getBpsRedirectUri = (): string => {
    // BPS only has base URL registered (without appId)
    // This is different from Public Secure which has appId in redirect_uri
    // The appId is preserved in sessionStorage (bps_appId) and preAuthUrl for use after callback
    return getEnvVar('BPS_REDIRECT_URI') || '';
};

export const getBpsConfig = (programContainer?: ProgramContainer): BpsConfig | null => {
    if (programContainer?.BpsConfig) {
        setStoredValue(BPS_CONFIG_KEY, JSON.stringify(programContainer.BpsConfig));
        return programContainer.BpsConfig;
    }
    const cached = getStoredValue(BPS_CONFIG_KEY);
    if (cached) {
        try {
            return JSON.parse(cached) as BpsConfig;
        } catch {
        }
    }
    return null;
};

export const validateBpsConfig = (config: BpsConfig | null): config is BpsConfig => {
    if (!config) {
        console.error('BPS configuration is missing');
        return false;
    }
    if (!config.domain || !config.clientId || !config.tokenEndpoint) {
        console.error('BPS configuration is incomplete. Required: domain, clientId, tokenEndpoint');
        return false;
    }
    return true;
};

const getSigninAttemptKey = (appId: string) =>
    `${SIGNIN_ATTEMPT_PREFIX}${(appId || '').toLowerCase()}`;

const normalizeBpsTokens = (tokens?: AuthToken | null): AuthToken | null => {
    if (!tokens) {
        return null;
    }

    const accessToken = tokens.access_token || tokens.accessToken || null;
    const idToken = tokens.id_token || tokens.idToken || null;

    return {
        ...tokens,
        access_token: accessToken || undefined,
        accessToken: accessToken || undefined,
        id_token: idToken || undefined,
        idToken: idToken || undefined,
    };
};

const clearSigninInFlightState = (clearVerifier = false) => {
    try {
        sessionStorage.removeItem(BPS_SIGNIN_IN_FLIGHT);
        if (clearVerifier) {
            removeStoredValue(BPS_CODE_VERIFIER);
        }
    } catch {
    }
};

const incrementSigninAttempts = (appId: string): boolean => {
    try {
        const key = getSigninAttemptKey(appId);
        const raw = sessionStorage.getItem(key);
        const current = raw ? parseInt(raw, 10) || 0 : 0;
        if (current >= MaxTry) {
            console.error(`Max BPS sign-in attempts reached for ${appId}`);
            return false;
        }
        sessionStorage.setItem(key, (current + 1).toString());
        return true;
    } catch {
        return true;
    }
};

const resetSigninAttempts = (appId: string) => {
    try {
        sessionStorage.removeItem(getSigninAttemptKey(appId));
        clearSigninInFlightState();
    } catch {
    }
};

export const generateCodeVerifier = (): string => {
    const existing = getStoredValue(BPS_CODE_VERIFIER);
    if (existing) {
        return existing;
    }
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    const codeVerifier = btoa(String.fromCharCode(...Array.from(array)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
    setStoredValue(BPS_CODE_VERIFIER, codeVerifier);
    return codeVerifier;
};
export const generateCodeChallenge = async (verifier: string): Promise<string> => {
    const padded = verifier.padEnd(50, '0');
    setStoredValue(BPS_CODE_VERIFIER, padded);
    return padded;
};

export const buildAuthorizationUrl = async (bpsConfig: BpsConfig): Promise<string> => {
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const redirectUri = getBpsRedirectUri();
    const params = [
        `response_type=code`,
        `domain=${encodeURIComponent(bpsConfig.domain)}`,
        `client_id=${encodeURIComponent(bpsConfig.clientId)}`,
        `scope=${encodeURIComponent(BPS_SCOPE)}`,
        `redirect_uri=${encodeURIComponent(redirectUri)}`,
        `code_challenge=${codeChallenge}`,
        `code_challenge_method=plain`
    ].join('&');
    return `${BPS_BASE_URL}${BPS_AUTH_ENDPOINT}?${params}`;
};

export const testBpsConnection = async (bpsConfig: BpsConfig, appId?: string): Promise<{ success: boolean; message: string; url?: string }> => {
    if (!bpsConfig.domain) {
        return { success: false, message: 'Domain is required' };
    }
    if (!bpsConfig.clientId) {
        return { success: false, message: 'Client ID is required' };
    }
    if (!bpsConfig.tokenEndpoint) {
        return { success: false, message: 'Token Endpoint is required' };
    }
    removeStoredValue(BPS_CODE_VERIFIER);

    // For test connection, use base redirect URI without app name
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const redirectUri = getEnvVar('BPS_REDIRECT_URI') || '';

    const params = [
        `response_type=code`,
        `domain=${encodeURIComponent(bpsConfig.domain)}`,
        `client_id=${encodeURIComponent(bpsConfig.clientId)}`,
        `scope=${encodeURIComponent(BPS_SCOPE)}`,
        `redirect_uri=${encodeURIComponent(redirectUri)}`,
        `code_challenge=${codeChallenge}`,
        `code_challenge_method=plain`
    ].join('&');

    const authUrl = `${BPS_BASE_URL}${BPS_AUTH_ENDPOINT}?${params}`;

    return {
        success: true,
        message: 'Configuration is valid. Opening BPS login page...',
        url: authUrl
    };
};

export const signin = async (programContainer: ProgramContainer, isEng: boolean): Promise<void> => {
    console.log('=== BPS STEP 1: signin() called ===');
    const appId = programContainer.CategoryId;
    const bpsConfig = getBpsConfig(programContainer);

    console.log('BPS signin params:', { appId, isEng, hasBpsConfig: !!bpsConfig });

    if (!validateBpsConfig(bpsConfig)) {
        console.error('BPS signin FAILED: invalid configuration');
        return;
    }
    if (getStoredValue(BPS_SIGNIN_IN_FLIGHT) === '1') {
        console.log('BPS signin SKIPPED: already in flight');
        return;
    }
    if (!incrementSigninAttempts(appId)) {
        console.error('BPS signin FAILED: max attempts reached');
        return;
    }
    setStoredValue(BPS_SIGNIN_IN_FLIGHT, '1');
    setStoredValue(BPS_APP_ID, appId);
    setStoredValue(BPS_LANG_KEY, isEng ? 'true' : 'false');

    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const redirectUri = getBpsRedirectUri();

    console.log('BPS signin redirect_uri:', redirectUri);
    console.log('BPS signin codeVerifier stored:', codeVerifier ? 'yes' : 'no');

    const params = [
        `response_type=code`,
        `domain=${encodeURIComponent(bpsConfig.domain)}`,
        `client_id=${encodeURIComponent(bpsConfig.clientId)}`,
        `scope=${encodeURIComponent(BPS_SCOPE)}`,
        `redirect_uri=${encodeURIComponent(redirectUri)}`,
        `code_challenge=${codeChallenge}`,
        `code_challenge_method=plain`
    ].join('&');

    const authUrl = `${BPS_BASE_URL}${BPS_AUTH_ENDPOINT}?${params}`;

    sessionStorage.removeItem(BPS_AUTH_TOKENS);
    sessionStorage.removeItem(BPS_USER_PROFILE);

    console.log('BPS signin REDIRECTING to:', authUrl);
    window.location.href = authUrl;
};

export const getTokens = async (programContainer: ProgramContainer): Promise<AuthToken | null> => {
    console.log('=== BPS STEP 2: getTokens() called ===');
    console.log('BPS getTokens current URL:', window.location.href);

    const code = new URL(window.location.href).searchParams.get("code");
    const existingTokens = sessionStorage.getItem(BPS_AUTH_TOKENS);
    const appId = programContainer.CategoryId || getStoredValue(BPS_APP_ID);
    const bpsConfig = getBpsConfig(programContainer);

    console.log('BPS getTokens params:', {
        code: code ? code.substring(0, 20) + '...' : null,
        hasExistingTokens: !!existingTokens,
        appId,
        hasBpsConfig: !!bpsConfig,
        bpsConfig: bpsConfig ? { domain: bpsConfig.domain, clientId: bpsConfig.clientId, tokenEndpoint: bpsConfig.tokenEndpoint } : null
    });

    try {
        const verifier = getStoredValue(BPS_CODE_VERIFIER);
        console.log('BPS getTokens codeVerifier in session:', verifier ? 'exists' : 'MISSING');
        if (!verifier && code) {
            console.error('BPS getTokens FAILED: code verifier missing, restarting login');
            clearSigninInFlightState(true);
            await signin(programContainer, true);
            return null;
        }
    } catch (error) {
        console.error('BPS getTokens ERROR checking verifier:', error);
        clearSigninInFlightState(true);
        await signin(programContainer, true);
        return null;
    }

    if (!code && !existingTokens) {
        console.log('BPS getTokens: No code and no existing tokens, returning null');
        return null;
    }

    if (existingTokens) {
        try {
            const token = normalizeBpsTokens(JSON.parse(existingTokens) as AuthToken);
            if (token?.access_token && verifyBpsToken(token.access_token)) {
                console.log('BPS getTokens: Using existing valid token from session');
                sessionStorage.removeItem(BPS_SIGNIN_IN_FLIGHT);
                return token;
            }
            console.log('BPS getTokens: Existing token invalid or expired');
        } catch {
            console.log('BPS getTokens: Failed to parse existing tokens');
        }
    }

    if (!code) {
        console.log('BPS getTokens: No code available, returning null');
        return null;
    }

    if (!validateBpsConfig(bpsConfig)) {
        console.error('BPS getTokens FAILED: invalid BPS configuration', bpsConfig);
        clearSigninInFlightState(true);
        return null;
    }

    console.log('=== BPS STEP 3: Token Exchange ===');
    try {
        const codeVerifier = getStoredValue(BPS_CODE_VERIFIER);
        const redirectUri = getBpsRedirectUri();

        console.log('BPS token exchange request:', {
            tokenEndpoint: bpsConfig.tokenEndpoint,
            redirectUri: redirectUri,
            hasCodeVerifier: !!codeVerifier
        });

        const response = await axios.post<AuthToken>(
            bpsConfig.tokenEndpoint,
            {
                authCode: code,
                codeVerifier: codeVerifier,
                redirectUri: redirectUri
            },
            { headers: { "Content-Type": "application/json" } }
        );

        console.log('BPS token exchange response status:', response.status);

        if (response.status !== 200) {
            console.error('BPS token exchange FAILED: status', response.status);
            clearSigninInFlightState(true);
            return null;
        }

        const tokens = normalizeBpsTokens(response.data);
        if (!tokens?.access_token) {
            console.error('BPS token exchange FAILED: response does not contain an access token');
            clearSigninInFlightState(true);
            return null;
        }

        sessionStorage.setItem(BPS_AUTH_TOKENS, JSON.stringify(tokens));
        resetSigninAttempts(appId);
        removeStoredValue(BPS_SIGNIN_IN_FLIGHT);
        removeStoredValue(BPS_CODE_VERIFIER);

        console.log('=== BPS STEP 4: Tokens received and stored ===');
        console.log('BPS tokens:', { hasAccessToken: !!tokens.access_token, hasIdToken: !!tokens.idToken });
        return tokens;
    } catch (error: any) {
        console.error('BPS Token Exchange ERROR:', error.response?.data || error.message);
        clearSigninInFlightState(true);
        return null;
    }
};

export const checkProfileAccount = async (
    programContainer: ProgramContainer,
    accessToken?: AuthToken
): Promise<ProgramContainer> => {
    let authTokens = normalizeBpsTokens(accessToken);

    if (!accessToken) {
        authTokens = await getTokens(programContainer);
        if (!authTokens?.access_token) {
            clearSigninInFlightState(true);
            await signin(programContainer, true);
            return programContainer;
        }
    } else if (!verifyBpsToken(authTokens.access_token)) {
        clearSigninInFlightState(true);
        await signin(programContainer, true);
        return programContainer;
    }

    // Set the access token on the profile - this is all that's needed for API calls
    const profile = {
        ...programContainer.Profile,
        token: authTokens.access_token
    };

    // Optionally extract user info from idToken if available
    if (authTokens.idToken) {
        try {
            const decoded = jwtDecode<{ sub: string; email?: string; name?: string }>(authTokens.idToken);
            profile.userId = decoded.sub;
            if (decoded.email) profile.email = decoded.email;
            if (decoded.name) profile.name = decoded.name;
        } catch (error: any) {
            console.warn("Failed to decode BPS idToken:", error.message);
        }
    }

    programContainer.Profile = profile;
    sessionStorage.setItem(BPS_USER_PROFILE, JSON.stringify(profile));
    console.log("BPS: Profile updated with token");

    return programContainer;
};

export const getUserInfo = async (userInfoEndpoint: string, accessToken: string): Promise<any> => {
    try {
        const response = await axios.get(userInfoEndpoint, {
            headers: { 'Authorization': `Bearer ${accessToken}` }
        });
        return response.data;
    } catch (error: any) {
        console.error("Failed to get BPS user info:", error.message);
        return null;
    }
};

interface BpsAuthToken extends AuthToken {
    refresh_token?: string | null;
}

export const refreshToken = async (programContainer: ProgramContainer): Promise<string | null> => {
    const tokens = sessionStorage.getItem(BPS_AUTH_TOKENS);
    if (!tokens) {
        return null;
    }

    let parsedTokens: BpsAuthToken;
    try {
        parsedTokens = JSON.parse(tokens) as BpsAuthToken;
    } catch {
        return null;
    }
    if (!parsedTokens.refresh_token) {
        console.warn("No BPS refresh token available");
        return null;
    }

    const bpsConfig = getBpsConfig(programContainer);
    if (!bpsConfig?.refreshEndpoint) {
        console.warn("No BPS refresh endpoint configured");
        return null;
    }

    try {
        const response = await axios.post<BpsAuthToken>(
            bpsConfig.refreshEndpoint,
            { refreshToken: parsedTokens.refresh_token },
            { headers: { "Content-Type": "application/json" } }
        );
        const newTokens = response.data;
        sessionStorage.setItem(BPS_AUTH_TOKENS, JSON.stringify(newTokens));
        return newTokens.access_token || null;
    } catch (error: any) {
        console.error("BPS Token refresh failed:", error.message);
        return null;
    }
};

export const verifyBpsToken = (token: string): boolean => {
    if (!token) return false;
    try {
        const decoded = jwtDecode<{ exp: number }>(token);
        const deadline = Math.floor(Date.now() / 1000) + 300;
        if (decoded.exp < deadline) {
            console.log('BPS token has expired or expiring soon');
            return false;
        }
        return true;
    } catch (error: any) {
        console.error('BPS token verification failed:', error.message);
        return false;
    }
};

export const logout = async (
    programContainer: ProgramContainer,
    customRedirectUri?: string
): Promise<void> => {
    const tokens = sessionStorage.getItem(BPS_AUTH_TOKENS);
    const bpsConfig = getBpsConfig(programContainer);

    if (tokens && bpsConfig?.logoutEndpoint) {

        try {
            const parsedTokens = JSON.parse(tokens) as AuthToken;
            const idToken = parsedTokens.idToken || parsedTokens.id_token;
            if (idToken) {
                const response = await axios.post(bpsConfig.logoutEndpoint, {
                    idToken,
                    postLogoutRedirectUri: customRedirectUri || window.location.origin
                });
                const { logoutUrl } = response.data;
                clearSessionStorage();
                if (logoutUrl) {
                    window.location.href = logoutUrl;
                    return;
                }
            }
        } catch (error: any) {
            console.error("BPS Logout request failed:", error.message);
        }
    }
    clearSessionStorage();
    window.location.href = customRedirectUri || '/';
};

export const clearSessionStorage = (): void => {
    sessionStorage.removeItem(BPS_AUTH_TOKENS);
    sessionStorage.removeItem(BPS_USER_PROFILE);
    removeStoredValue(BPS_CODE_VERIFIER);
    removeStoredValue(BPS_APP_ID);
    removeStoredValue(BPS_CONFIG_KEY);
    removeStoredValue(BPS_SIGNIN_IN_FLIGHT);
    removeStoredValue(BPS_LANG_KEY);
    clearSelectedService();
    try {
        const keys = Object.keys(sessionStorage);
        keys
            .filter(key => key.startsWith(SIGNIN_ATTEMPT_PREFIX))
            .forEach(key => sessionStorage.removeItem(key));
    } catch {
    }
};

export const getBpsAccessToken = (): string | null => {
    const tokens = sessionStorage.getItem(BPS_AUTH_TOKENS);
    if (tokens) {
        try {
            const parsed = JSON.parse(tokens) as AuthToken;
            return parsed?.access_token || null;
        } catch {
            return null;
        }
    }
    return null;
};

export const getBpsIdToken = (): string | null => {
    const tokens = sessionStorage.getItem(BPS_AUTH_TOKENS);
    if (tokens) {
        try {
            const parsed = JSON.parse(tokens) as AuthToken;
            return parsed?.idToken || parsed?.id_token || null;
        } catch {
            return null;
        }
    }
    return null;
};

export const isBpsAuthenticated = (): boolean => {
    const token = getBpsAccessToken();
    return token ? verifyBpsToken(token) : false;
};

export const getBpsUserProfile = (): any | null => {
    const profile = sessionStorage.getItem(BPS_USER_PROFILE);
    if (profile) {
        try {
            return JSON.parse(profile);
        } catch {
            return null;
        }
    }
    return null;
};